/**
 * Meybra Demo Bar
 *
 * Persistent top strip that appears on every page while a demo session is
 * active. Combines:
 *   - The subtle "Demo Environment" banner (never modifies real data)
 *   - The Panel Switcher — the visitor can hop between Club Dashboard /
 *     Team Coach / Team Manager / Athlete-Parent without re-logging in
 *   - An Exit button that ends the demo cleanly (analytics + token wipe)
 *
 * Switching a panel mints a fresh JWT server-side that impersonates the
 * account matching the requested perspective and returns the new landing
 * route. We hard-navigate on switch so the entire layout re-hydrates from
 * the new token.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, Users, ClipboardList, User, LogOut, Loader2, Sparkles } from 'lucide-react';
import { useDemo } from '@/context/DemoContext';

const PANEL_META = {
  club_dashboard: { label: 'Club Dashboard', icon: Building2 },
  team_coach: { label: 'Team Coach', icon: Users },
  team_manager: { label: 'Team Manager', icon: ClipboardList },
  private_coach: { label: 'Private Coach', icon: Users },
  athlete_parent: { label: 'Athlete / Parent', icon: User },
};

const PANEL_TOAST = {
  club_dashboard: 'You are now viewing the Club Dashboard experience.',
  team_coach: 'You are now viewing the Team Coach experience.',
  team_manager: 'You are now viewing the Team Manager experience.',
  private_coach: 'You are now viewing the Private Coach experience.',
  athlete_parent: 'You are now viewing the Athlete / Parent experience.',
};

const DemoBar = () => {
  const navigate = useNavigate();
  const { session, isDemo, switchPanel, endDemo } = useDemo();
  const [busy, setBusy] = useState(false);

  if (!isDemo || !session) return null;

  const handleSwitch = async (panel) => {
    if (panel === session.active_panel || busy) return;
    setBusy(true);
    try {
      const res = await switchPanel(panel);
      toast.success(PANEL_TOAST[panel] || 'Switched experience.');
      if (res?.redirect) {
        // Hard nav so AuthProvider hydrates from the newly-minted token.
        window.location.href = res.redirect;
      }
    } catch (e) {
      toast.error('Could not switch experience.');
    } finally {
      setBusy(false);
    }
  };

  const handleExit = async () => {
    setBusy(true);
    try {
      await endDemo({ kind: 'exit' });
    } finally {
      setBusy(false);
      navigate('/demo');
    }
  };

  const panels = session.available_panels || [];
  const showSwitcher = panels.length > 1;

  return (
    <div className="sticky top-0 z-[100] w-full bg-primary/95 text-black border-b border-primary/50 backdrop-blur">
      <div className="max-w-full mx-auto px-3 sm:px-5 py-1.5 flex items-center gap-2 sm:gap-4 flex-wrap">
        <div className="flex items-center gap-2 shrink-0" data-testid="demo-bar-badge">
          <Sparkles size={13} className="shrink-0" />
          <span className="text-[10px] sm:text-[11px] uppercase tracking-widest font-bold">Demo Environment</span>
          <span className="hidden md:inline text-[10px] text-black/70">
            · Fictional sandbox. Nothing here affects real users.
          </span>
        </div>

        {showSwitcher && (
          <div className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto scrollbar-none" data-testid="demo-panel-switcher">
            {panels.map((p) => {
              const meta = PANEL_META[p] || { label: p, icon: User };
              const Icon = meta.icon;
              const active = p === session.active_panel;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => handleSwitch(p)}
                  disabled={busy}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[11px] uppercase tracking-wider font-bold whitespace-nowrap transition-colors ${
                    active
                      ? 'bg-black text-primary'
                      : 'bg-black/10 text-black/80 hover:bg-black/20'
                  } ${busy ? 'opacity-60 cursor-wait' : ''}`}
                  data-testid={`demo-switch-${p}`}
                >
                  <Icon size={12} />
                  {meta.label}
                </button>
              );
            })}
          </div>
        )}

        <button
          type="button"
          onClick={handleExit}
          disabled={busy}
          className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[11px] uppercase tracking-wider font-bold bg-black text-primary hover:bg-black/80 shrink-0"
          data-testid="demo-exit-btn"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <LogOut size={12} />}
          Exit
        </button>
      </div>
    </div>
  );
};

export default DemoBar;
